// @flow

import _ from 'lodash/fp';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Redirect, withRouter} from 'react-router-dom';
import {touch, isValid, getFormValues, reset as resetForm} from 'redux-form';
import {
  reset,
  clear,
  createOrder,
  fetchData,
  updateOrderRows,
  updateOrder,
  setNoteRow,
  updateOrderRow,
  setActiveComment,
  addOrderComment,
  updateOrderComment,
  removeOrderComment,
  updateReportFilter,
  updateUserOrderFilter,
  createExtraRow,
  updateExtraRow,
  removeExtraRow,
  setActiveExtraRow,
  fetchActiveExtraRowProducts,
  fetchActiveExtraRowVariants,
  clearOrderRows,
} from '../../actions/orderView';
import {setDirty} from '../../actions/form';
import SaveMenu from '../../components/SaveMenu/SaveMenu';
import OrderForm from '../../components/OrderForm/OrderForm';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import Navigation from '../../components/Navigation/Navigation';
import AdminList from '../../components/AdminList/AdminList';
import ProductRowDialog from '../../components/ProductRowDialog/ProductRowDialog';
import ActionConfirmation from '../ActionConfirmation/ActionConfirmation';
import validator from '../../helpers/validator';
import {
  NEW,
} from '../../orderStates.js';
import DisableContent from '../../components/DisableContent/DisableContent';
import ProductRowList from '../../components/ProductRowList/ProductRowList';
import OrderRowNoteDialog from '../../components/OrderRowNoteDialog/OrderRowNoteDialog';
import OrderCommentDialog from '../../components/OrderCommentDialog/OrderCommentDialog';
import moment from 'moment';

const orderValidation = {
  apartmentId: {rule: 'numeric|required', label: 'Lägenhet'},
  projectId: {rule: 'numeric|required', label: 'Projekt'},
  salesUserId: {rule: 'numeric|required', label: 'Säljare'},
  userId: {rule: 'numeric|required', label: 'Kund'},
};

const noteRowValidation = {
  note: {rule: 'string', label: 'Anteckning'},
};

const orderCommentValidation = {
  workerTypeId: {rule: 'numeric|required_without:roomTypeId', label: 'Yrkeskategori'},
  roomTypeId: {rule: 'numeric|required_without:workerTypeId', label: 'Rumstyp'},
  note: {rule: 'string|required', label: 'Kommentar'},
};

const extraRowValidation = {
  roomId: {rule: 'numeric|required', label: 'Rum'},
  product: {rule: 'required', label: 'Produkt'},
  productVariant: {rule: 'required', label: 'Variant'},
  quantity: {rule: 'numeric|min:0|required', label: 'Antal'},
};

const orderCommentColumns = [{
  property: 'roomType.name',
  header: {
    label: 'Rumstyp',
  },
  props: {
    style: {
      width: 200,
    },
  },
}, {
  property: 'workerType.name',
  header: {
    label: 'Yrkeskategori',
  },
  props: {
    style: {
      width: 200,
    },
  },
}, {
  property: 'note',
  header: {
    label: 'Kommentar',
  },
}];

const customRowColumns = [{
  property: 'name',
  header: {
    label: 'Namn',
  },
}, {
  property: 'totalSellingPrice',
  header: {
    label: 'Pris',
  },
}, {
  property: 'quantity',
  header: {
    label: 'Antal',
  },
}];

const orderChangeColumns = [
  {
    property: 'type',
    cell: {
      formatters: [val => {
        switch (val) {
          case 'add':
            return 'Tillagd';
          case 'remove':
            return 'Borttagen';
          case 'update':
            return 'Ändrad';
          default:
            return val;
        }
      },
      ],
    },
    header: {
      label: 'Typ',
    },
  },
  {
    property: 'user',
    cell: {
      formatters: [val => val ?
        val.email  :
        '-',
      ],
    },
    header: {
      label: 'Email',
    },
  },
  {
    property: 'productSlot',
    cell: {
      formatters: [val => val ?
        val.room.name :
        '-',
      ],
    },

    header: {
      label: 'Rum',
    },
  },
  {
    property: 'oldVariant',
    cell: {
      formatters: [val => val ?
        `${val.product.name} - ${val.name}` :
        '-',
      ],
    },

    header: {
      label: 'Från',
    },
  },
  {
    property: 'newVariant',
    cell: {
      formatters: [val => val ?
        `${val.product.name} - ${val.name}` :
        '-',
      ],
    },
    header: {
      label: 'Till',
    },
  },
  {
    property: 'created',
    cell: {
      formatters: [val => moment(val).format('YYYY-MM-DD')],
    },
    header: {
      label: 'Tid',
    },
  },
];

export type Props = {
  history: Object,
  orderComplete: boolean,
  updateOrderRows: typeof updateOrderRows,
  clearOrderRows:  typeof clearOrderRows,
  setDirty: (dirty: boolean) => any,
  reset: typeof reset,
  resetForm: typeof resetForm,
  isDirty: boolean,
  order: ?any,
  apartments: Object,
  clear: typeof clear,
  createOrder: typeof createOrder,
  error: ?any,
  fetchData: typeof fetchData,
  isFetching: boolean,
  params: any,
  prevPath: string,
  projects: any[],
  updateOrder: typeof updateOrder,
  users: any[],
  salesUsers: any[],
  specialProducts: Object,
  productTypes: any[],
  orderFormValues: any,
  orderFormIsValid: boolean,
  touch: typeof touch,
  customRows: any[],
  rows: any[],
  noteRow: ?any,
  noteRowError: ?any,
  noteRowIsFetching: boolean,
  noteRowIsExtra: boolean,
  noteRowFormValues: any,
  noteRowFormIsValid: boolean,
  setNoteRow: typeof setNoteRow,
  updateOrderRow: typeof updateOrderRow,
  orderComment: ?any,
  orderCommentError: ?any,
  orderCommentIsFetching: boolean,
  orderCommentFormValues: any,
  orderCommentFormIsValid: boolean,
  setActiveComment: typeof setActiveComment,
  addOrderComment: typeof addOrderComment,
  updateOrderComment: typeof updateOrderComment,
  removeOrderComment: typeof removeOrderComment,
  extraRows: any[],
  allRowsByRoom: any[],
  extraRowFormValues: any,
  extraRowFormIsValid: boolean,
  extraRow: ?any,
  extraRowError: ?any,
  extraRowIsFetching: boolean,
  setActiveExtraRow: typeof setActiveExtraRow,
  createExtraRow: typeof createExtraRow,
  updateExtraRow: typeof updateExtraRow,
  removeExtraRow: typeof removeExtraRow,
  fetchExtraRowProducts: typeof fetchActiveExtraRowProducts,
  extraRowProducts: any[],
  extraRowProductsAreFetching: boolean,
  fetchExtraRowVariants: typeof fetchActiveExtraRowVariants,
  extraRowVariants: any[],
  extraRowVariantsAreFetching: boolean,
  rooms: any[],
  roomTypes: any[],
  workerTypes: any[],
  suppliers: any[],
  reportFilter: {
    includePlans: boolean,
    supplierId: ?number,
  },
  updateReportFilter: typeof updateReportFilter,
  userOrderFilter: {
    includePlans: boolean,
  },
  updateUserOrderFilter: typeof updateUserOrderFilter,
  orders: any[],
  orderTotalPrice: number,
};

class OrderContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    this.props.fetchData(this.props.params.id);
  }

  componentWillUnmount() {
    this.props.clear();
  }

  setDirty() {
    const {
      setDirty,
      isDirty,
    } = this.props;

    if (!isDirty) {
      setDirty(true);
    }
  }

  reset() {
    const {
      setDirty,
      reset,
      resetForm,
    } = this.props;
    reset();
    resetForm('order');
    setDirty(false);
  }

  save() {
    const {
      orderFormValues,
      touch,
      orderFormIsValid,
      order,
      updateOrder,
      createOrder,
    } = this.props;
    const keys = _.keys(orderValidation);
    touch('order', ...keys);
    if (!orderFormIsValid) {
      return;
    }

    if (!order) {
      createOrder(orderFormValues);
    } else {
      updateOrder(order.id, orderFormValues);
    }
  }

  saveRowNote() {
    const {
      noteRowIsExtra,
      noteRowFormValues,
      noteRowFormIsValid,
      noteRow,
      updateOrderRow,
      updateExtraRow,
      touch,
    } = this.props;
    const keys = _.keys(noteRowValidation);
    touch('noteRow', ...keys);
    if (!noteRowFormIsValid || !noteRow) {
      return;
    }
    const data = _.pick(['note'], noteRowFormValues);

    if (noteRowIsExtra) {
      updateExtraRow(noteRow.id, data);
    } else {
      updateOrderRow(noteRow.id, data);
    }
  }

  saveOrderComment() {
    const {
      order,
      orderComment,
      orderCommentFormIsValid,
      orderCommentFormValues,
      addOrderComment,
      updateOrderComment,
      touch,
    } = this.props;
    const keys = _.keys(orderCommentValidation);
    touch('orderComment', ...keys);
    if (!orderCommentFormIsValid || !order || !orderComment) {
      return;
    }

    const data = _.pick(keys, orderCommentFormValues);

    if (!!orderComment.id) {
      updateOrderComment(orderComment.id, data);
    } else {
      addOrderComment(order.id, data);
    }
  }

  saveExtraRow() {
    const {
      extraRowFormValues,
      extraRowFormIsValid,
      order,
      extraRow,
      createExtraRow,
      updateExtraRow,
      touch,
    } = this.props;
    const keys = _.keys(extraRowValidation);
    touch('productRow', ...keys);
    if (!extraRowFormIsValid) {
      return;
    }
    const data = {
      ...extraRowFormValues,
      productVariantId: extraRowFormValues.productVariant.id,
    };

    if (extraRow && !extraRow.id && !!order) {
      createExtraRow(order.id, data);
    } else if (!!extraRow) {
      updateExtraRow(extraRow.id, data);
    }
  }

  render() {
    const {
      history,
      orderComplete,
      updateOrderRows,
      isDirty,
      order,
      apartments,
      error,
      isFetching,
      params: {id},
      prevPath,
      projects,
      specialProducts,
      productTypes,
      updateOrder,
      users,
      salesUsers,
      customRows,
      rows,
      noteRow,
      noteRowError,
      noteRowIsFetching,
      noteRowFormValues,
      setNoteRow,
      orderComment,
      orderCommentError,
      orderCommentIsFetching,
      orderCommentFormValues,
      setActiveComment,
      removeOrderComment,
      roomTypes,
      workerTypes,
      suppliers,
      reportFilter,
      updateReportFilter,
      userOrderFilter,
      updateUserOrderFilter,
      orders,
      clear,
      fetchData,
      extraRows,
      allRowsByRoom,
      extraRowFormValues,
      extraRow,
      extraRowError,
      extraRowIsFetching,
      setActiveExtraRow,
      removeExtraRow,
      fetchExtraRowProducts,
      extraRowProducts,
      extraRowProductsAreFetching,
      fetchExtraRowVariants,
      extraRowVariants,
      extraRowVariantsAreFetching,
      rooms,
      orderTotalPrice,
      clearOrderRows,
    } = this.props;

    return (
      <div className="relative">
        <div className="row align-justify align-middle content-header">
          <div className="column shrink">
            <div className="row no-row-margins align-middle">
              <Navigation
                current={order}
                list={orders}
                prevPath={prevPath}
                clear={clear}
                fetchData={fetchData} />
              <div className="content-title">
                {(order && 'Beställning') || 'Ny beställning'}
              </div>
            </div>
          </div>
          <div className="column shrink horizontal-button-list">
            {order &&
              <DisableContent disabled={isDirty}>
                <ActionConfirmation
                  id="archiveOrder"
                  className="secondary button"
                  message={`Vill du verkligen ${order.archived ? 'av' : ''}arkivera den här beställningen?`}
                  buttonText={order.archived ? 'Avarkivera' : 'Arkivera'}
                  action={() => updateOrder(order.id, {archived: !order.archived})} />
              </DisableContent>
            }
          </div>
        </div>
        <LoadingIndicator isLoading={isFetching || extraRowProductsAreFetching} />
        <ServerError error={error} />
        <div className="content-section box">
          {id ?
            <OrderForm
              rows={rows}
              updateOrder={updateOrder}
              updateOrderRows={updateOrderRows}
              clearOrderRows={clearOrderRows}
              orderComplete={orderComplete}
              initialValues={order}
              order={order}
              apartments={apartments}
              projects={projects}
              users={users}
              salesUsers={salesUsers}
              specialProducts={specialProducts}
              productTypes={productTypes}
              onOpenCustomerOrder={() => history.push(`${prevPath}/${id}/customerorder`)}
              onOpenCustomerApartment={() => history.push(`/projects/${order.apartment.projectId}/apartments/${order.apartmentId}`)}
              suppliers={suppliers}
              reportFilter={reportFilter}
              updateReportFilter={updateReportFilter}
              userOrderFilter={userOrderFilter}
              updateUserOrderFilter={updateUserOrderFilter}
              onUserChange={() => this.setDirty()}
              orderTotalPrice={orderTotalPrice}
              validate={values => validator(orderValidation, values)} />
            :
            (order ?
              <Redirect to={prevPath} />
              :
              <OrderForm
                rows={rows}
                updateOrder={updateOrder}
                updateOrderRows={updateOrderRows}
                clearOrderRows={clearOrderRows}
                orderComplete={orderComplete}
                apartments={apartments}
                projects={projects}
                users={users}
                salesUsers={salesUsers}
                specialProducts={specialProducts}
                productTypes={productTypes}
                onUserChange={() => this.setDirty()}
                suppliers={suppliers}
                reportFilter={reportFilter}
                updateReportFilter={updateReportFilter}
                userOrderFilter={userOrderFilter}
                updateUserOrderFilter={updateUserOrderFilter}
                orderTotalPrice={orderTotalPrice}
                validate={values => validator(orderValidation, values)} />
            )
          }
        </div>
        {order &&
          <div className="content-section box">
            <div className="content-section">
              <div className="box-title">
                Kommentar för rum och yrkeskategori
              </div>
              <AdminList
                onRowSelected={row => setActiveComment(row)}
                columns={orderCommentColumns}
                rows={order.comments} />
              <button
                onClick={() => setActiveComment({})}
                type="button"
                className="button small">
                Lägg till kommentar
              </button>
            </div>
          </div>
        }
        {order &&
          <div className="content-section box">
            <div className="content-section">
              <div className="box-title">
                Produkter
              </div>
              <ProductRowList
                allRowsByRoom={allRowsByRoom}
                onSelectRow={row => setNoteRow(row, false)}
                onSelectExtraRow={row => {
                  if (order.state === NEW) {
                    setActiveExtraRow(row);
                  } else {
                    setNoteRow(row, true);
                  }
                }}
                onRemoveExtraRow={row => removeExtraRow(row.id)}
                canRemoveExtraRows={order.state === NEW} />
              {order.state === NEW && (
                <button
                  onClick={() => setActiveExtraRow({})}
                  type="button"
                  className="button small">
                  Lägg till produkt
                </button>
              )}
            </div>
            <div className="content-section">
              <span className="box-title">
                Friprodukter
              </span>
              <AdminList
                onRowSelected={row => history.push(`${prevPath}/${id}/customrows/${row.id}`)}
                columns={customRowColumns}
                rows={customRows} />
              {order.state === NEW && (
                <button
                  onClick={() => history.push(`${prevPath}/${id}/customrows/new`)}
                  type="button"
                  className="button small">
                  Lägg till friprodukt
                </button>
              )}
            </div>
          </div>
        }
        {isDirty &&
          <SaveMenu
            isLoading={isFetching}
            onDiscard={() => this.reset()}
            onSave={() => this.save()} />
        }
        {extraRow &&
          <ProductRowDialog
            existingRows={extraRows}
            products={extraRowProducts}
            variants={extraRowVariants}
            activeRow={extraRow}
            rooms={rooms}
            error={extraRowError}
            isLoading={extraRowIsFetching || extraRowVariantsAreFetching}
            formValues={extraRowFormValues}
            onSave={() => this.saveExtraRow()}
            onSelectProduct={productId => fetchExtraRowVariants(productId)}
            onClose={() => setActiveExtraRow(null)}
            validate={values => validator(extraRowValidation, values)}
            onOpen={fetchExtraRowProducts}
            combineProduct={true} />
        }
        {noteRow &&
          <OrderRowNoteDialog
            orderRow={noteRow}
            error={noteRowError}
            isLoading={noteRowIsFetching}
            formValues={noteRowFormValues}
            onSave={() => this.saveRowNote()}
            onClose={() => setNoteRow(null)}
            validate={values => validator(noteRowValidation, values)} />
        }
        {orderComment &&
          <OrderCommentDialog
            orderComment={orderComment}
            roomTypes={roomTypes}
            workerTypes={workerTypes}
            error={orderCommentError}
            isLoading={orderCommentIsFetching}
            formValues={orderCommentFormValues}
            onSave={() => this.saveOrderComment()}
            onClose={() => setActiveComment(null)}
            onRemove={() => removeOrderComment(orderComment.id)}
            validate={values => validator(orderCommentValidation, values)} />
        }
        {order && order.hasBeenLocked &&
          <div className="content-section box">
            <div className="content-section">
              <div className="box-title">
                Order förändringar
              </div>
              <AdminList
                columns={orderChangeColumns}
                rows={order.changes}
                sortColumnIndex={4} />
            </div>
          </div>
        }

      </div>
    );
  }
}


const mapStateToProps = ({
  orderView: {
    orderComplete,
    isDirty,
    active,
    apartments: {
      byProjectId: apartments,
      projects,
    },
    specialProducts: {
      byProductTypeId: specialProducts,
      productTypes,
    },
    customRows: {
      list: customRows,
    },
    extraRow: {
      active: extraRow,
      error: extraRowError,
      isFetching: extraRowIsFetching,
    },
    activeExtraRowProducts: {
      isFetching: extraRowProductsAreFetching,
      list: extraRowProducts,
    },
    activeExtraRowVariants: {
      isFetching: extraRowVariantsAreFetching,
      list: extraRowVariants,
    },
    error,
    isFetching,
    users,
    salesUsers,
    rooms,
    rows: {
      rows,
      extraRows,
      allRowsByRoom,
    },
    noteRow: {
      active: noteRow,
      error: noteRowError,
      isFetching: noteRowIsFetching,
      isExtra: noteRowIsExtra,
    },
    orderComment: {
      active: orderComment,
      error: orderCommentError,
      isFetching: orderCommentIsFetching,
      roomTypes,
      workerTypes,
    },
    suppliers,
    reportFilter,
    userOrderFilter,
    orderTotalPrice,
  },
  orderList: {
    filteredOrders: orders,
  },
  form,
}) => ({
  orderComplete,
  isDirty,
  order: active,
  apartments,
  specialProducts,
  productTypes,
  customRows,
  error,
  isFetching,
  orderFormIsValid: isValid('order')({form}),
  orderFormValues: getFormValues('order')({form}),
  projects,
  users,
  salesUsers,
  rows,
  noteRow,
  noteRowError,
  noteRowIsFetching,
  noteRowIsExtra,
  noteRowFormIsValid: isValid('noteRow')({form}),
  noteRowFormValues: getFormValues('noteRow')({form}),
  orderComment,
  orderCommentError,
  orderCommentIsFetching,
  orderCommentFormIsValid: isValid('orderComment')({form}),
  orderCommentFormValues: getFormValues('orderComment')({form}),
  roomTypes,
  workerTypes,
  suppliers,
  reportFilter,
  userOrderFilter,
  orders,
  allRowsByRoom,
  extraRow,
  extraRowError,
  extraRowIsFetching,
  extraRowProducts,
  extraRowProductsAreFetching,
  extraRowVariants,
  extraRowVariantsAreFetching,
  extraRowFormIsValid: isValid('productRow')({form}),
  extraRowFormValues: getFormValues('productRow')({form}),
  rooms,
  orderTotalPrice,
});

const mapDispatchToProps = dispatch => ({
  setDirty: dirty =>
    dispatch(setDirty(dirty, 'order')),
  resetForm: form =>
    dispatch(resetForm(form)),
  reset: () =>
    dispatch(reset()),
  updateOrder: (id, data) =>
    dispatch(updateOrder(id, data)),
  updateOrderRows: (id, data) =>
    dispatch(updateOrderRows(id, data)),
  clearOrderRows: (id, data) =>
    dispatch(clearOrderRows(id)),
  createOrder: data =>
    dispatch(createOrder(data)),
  clear: () =>
    dispatch(clear()),
  touch: (form, ...fields) =>
    dispatch(touch(form, ...fields)),
  fetchData: orderId =>
    dispatch(fetchData(orderId)),
  setNoteRow: (row, isExtra) =>
    dispatch(setNoteRow(row, isExtra)),
  updateOrderRow: (id, data) =>
    dispatch(updateOrderRow(id, data)),
  setActiveComment: comment =>
    dispatch(setActiveComment(comment)),
  addOrderComment: (id, data) =>
    dispatch(addOrderComment(id, data)),
  updateOrderComment: (id, data) =>
    dispatch(updateOrderComment(id, data)),
  removeOrderComment: id =>
    dispatch(removeOrderComment(id)),
  updateReportFilter: (supplierId: ?number, includePlans: boolean) =>
    dispatch(updateReportFilter(supplierId, includePlans)),
  updateUserOrderFilter: (includePlans: boolean) =>
    dispatch(updateUserOrderFilter(includePlans)),
  setActiveExtraRow: row =>
    dispatch(setActiveExtraRow(row)),
  createExtraRow: (orderId, data) =>
    dispatch(createExtraRow(orderId, data)),
  updateExtraRow: (id, data) =>
    dispatch(updateExtraRow(id, data)),
  removeExtraRow: id =>
    dispatch(removeExtraRow(id)),
  fetchExtraRowVariants: id =>
    dispatch(fetchActiveExtraRowVariants(id)),
  fetchExtraRowProducts: id =>
    dispatch(fetchActiveExtraRowProducts()),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderContainer));



// WEBPACK FOOTER //
// ./src/containers/OrderContainer/OrderContainer.js